<template>
  <div class="projects" data-locator="dashboard-page">
    <v-container
      :class="{'pa-4':$vuetify.display.mobile }"
      class="mb-8"
      fluid
    >
      <v-row>
        <v-col cols="12">
          <ProjectsHeader v-model:searchQuery="searchQuery"
                          :display="display"
                          :isRecentRoomsView="isRecentRoomsView"
                          @display="changeDisplay"
          />
          <ProjectsActiveFilters/>

          <!-- FOLDERS -->
          <ProjectsFolders v-if="!isRecentRoomsView && !searchModeEnabled" class="mb-8" />

          <!-- ROOMS -->
          <template v-if="isPending && !rooms.length">
            <AppCard v-show="display === DISPLAY.table">
              <v-skeleton-loader type="heading, table-row@2" />
            </AppCard>

            <v-row v-show="display === DISPLAY.grid">
              <v-col v-for="_ in 2" :key="_" cols="12" sm="6" md="3" lg="3" xl="2">
                <AppCard :height="PROJECT_CARD_TOTAL_HEIGHT + 'px'">
                  <v-skeleton-loader type="heading, text@5" />
                </AppCard>
              </v-col>
            </v-row>
          </template>
          <template v-else-if="rooms.length">
            <div class="text-grey-900 font-weight-semi-bold text--large my-4">
              {{ searchModeEnabled ? $t('projects.ProjectsHeader.searchTitle') : $t('projects.ProjectsHeader.projects') }}
            </div>
            <div
              v-if="!(searchModeEnabled && rooms.length === 0)"
              class="text-grey-800 font-weight-semi-bold text--medium"
            >
              {{ headerTitle }}
            </div>
            <ProjectsGridTemplate
              v-if="display === DISPLAY.grid || $vuetify.display.smAndDown"
              :rooms="roomsByRole"
              :hidePagination="isRecentRoomsRoute && !isSearchModeOrFiltersEnabled"
              :isRecentRoomsView="isRecentRoomsView"
              @update:page="onPageChange"
              @update:page-size="onPageSizeChange"
            />
            <ProjectsTableTemplate
              v-if="display === DISPLAY.table && !$vuetify.display.smAndDown"
              :rooms="roomsByRole"
              :hidePagination="isRecentRoomsRoute && !isSearchModeOrFiltersEnabled"
              :isRecentRoomsView="isRecentRoomsView"
              @update:page="onPageChange"
              @update:page-size="onPageSizeChange"
              @sort="onSortChange"
            />
          </template>
          <template
            v-if="searchModeEnabled && !(pmRoomsPending || guestRoomsPending) && rooms.length === 0">
            <div
              style="max-width: 575px"
              class="text-center mx-auto mt-8 mb-4"
            >
              <app-icon icon-name="magnifying-glass" class="d-inline-block mb-2" />
              <p class="text--large text--bold">
                {{ $t("projects.search.noResultOnSearch") }}
              </p>
              <p class="text--small">
                {{ $t("projects.search.noResultOnSearchHint") }}
              </p>
            </div>
          </template>
        </v-col>
      </v-row>
    </v-container>
    <ProjectsFiltersPane/>
    <DocumentsNewFolderDialog/>
    <ProjectsMoveRoomDialog v-if="moveRoomDialogIsOpen" isRoom/>
    <RoomUnderNdaDialog
      v-if="roomUnderNdaDialog.isOpen"
      @close="SET_ROOM_UNDER_NDA_DIALOG({ isOpen: false })"
    />
  </div>
</template>

<script>
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { debounce } from 'lodash-es'
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import { DEFAULT_PAGE_SIZE } from '@/common/constants'
import { toast } from '@/common/utils/toast'
import { getUserSetting, setUserSetting } from '@/common/utils/userSettings'
import DocumentsNewFolderDialog from '@/project/documents/dialogs/DocumentsNewFolderDialog.vue'
import ProjectsMoveRoomDialog from '@/projects/dialogs/ProjectsMoveRoomDialog.vue'
import RoomUnderNdaDialog from '@/projects/dialogs/RoomUnderNdaDialog.vue'
import ProjectsActiveFilters from '@/projects/ProjectsActiveFilters.vue'
import ProjectsFiltersPane from '@/projects/ProjectsFiltersPane.vue'
import ProjectsFolders from '@/projects/ProjectsFolders.vue'
import ProjectsGridTemplate from '@/projects/ProjectsGridTemplate.vue'
import ProjectsHeader from '@/projects/ProjectsHeader.vue'
import ProjectsTableTemplate from '@/projects/ProjectsTableTemplate.vue'
import { getCurrentRouteSafe } from '@/router'
import { GlobalMutationTypes } from '@/store/modules/global/mutation_types'
import { GET_PROJECTS_FOLDER_BY_ID } from '@/store/modules/projects-folders/action_types'
import { SET_CURRENT_PROJECTS_FOLDER } from '@/store/modules/projects-folders/mutation_types'
import { SET_ROOM_UNDER_NDA_DIALOG } from '@/store/modules/room/mutation_types'
import { GET_RECENT_ROOMS } from '@/store/modules/rooms/action_types'
import { SET_SEARCH_MODE_ENABLED } from '@/store/modules/rooms/mutation_types'

import { DISPLAY } from '../display'
import { PROJECT_CARD_TOTAL_HEIGHT } from '../ProjectsGrid.vue'

dayjs.extend(isBetween)
dayjs.extend(isSameOrAfter)
// We use a multiple of 3 and 4 to have a good layout for the cards to be displayed
const RECENT_ROOMS_LIMIT = 12
const SEARCH_DEBOUNCE_RATE = 1000

export default {
  name: 'Projects',
  components: {
    AppCard,
    DocumentsNewFolderDialog,
    ProjectsActiveFilters,
    ProjectsFiltersPane,
    ProjectsFolders,
    ProjectsGridTemplate,
    ProjectsHeader,
    ProjectsMoveRoomDialog,
    ProjectsTableTemplate,
    RoomUnderNdaDialog,
  },
  data: function () {
    return {
      PROJECT_CARD_TOTAL_HEIGHT,
      sortKey: 'name',
      filterKey: '',
      sorting: {
        pm: {
          sortBy: '',
          sortDir: '',
        },
        guest: {
          sortBy: '',
          sortDir: '',
        },
      },
      sortOrders: {
        state: 1,
        name: -1,
        creationDate: 1,
        dueDate: 1,
        members: 1,
        documents: 1,
        tasks: 1,
        originalDocuments: 1,
        documentsToSign: 1,
      },
      order: 1,
      display: this.$store.state.rooms.displayType || DISPLAY.grid,
      searchQuery: '',
    }
  },
  computed: {
    ...mapGetters('user', ['currentUserId']),
    ...mapGetters('login', ['isFullAccessLogin']),
    ...mapState('room', ['roomUnderNdaDialog']),
    ...mapState('rooms', [
      'filtersPane', 'sort', 'displayType', 'recentRoomsPending','searchModeEnabled',
      'pmRooms', 'guestRooms', 'pmPagination', 'guestPagination', 'pmRoomsPending', 'guestRoomsPending',
    ]),
    ...mapState('projectsFolders', ['currentProjectsFolder', 'projectsFolderByIdPending', 'moveRoomDialogIsOpen']),
    ...mapGetters('rooms', ['rooms']),
    // We use this one because we are init even before the router is properly finished,
    // since the app can deduce the layout before then and we are at the top level
    currentRoute () {
      return getCurrentRouteSafe()
    },
    isRecentRoomsView () {
      return this.currentRoute.path.includes('recent')
    },
    isPending () {
      return this.projectsFolderByIdPending || this.recentRoomsPending || this.pmRoomsPending || this.guestRoomsPending
    },
    headerTitle () {
      if (this.roomsByRole) {
        if (this.roomsByRole.asPM.length > 0) {
          return this.$t('projects.views.Projects.projectManager', { projectsCount: this.roomsByRole.asPM.length })
        } else if (this.roomsByRole.asGuest.length === 0) {
          return this.$t('projects.views.Projects.projectManager', { projectsCount: 0 })
        } else if (this.roomsByRole.asGuest.length > 0) {
          return this.$t('projects.views.Projects.guest', { projectsCount: this.roomsByRole.asGuest.length })
        }
      }
      return this.$t('projects.views.Projects.projectManager', { projectsCount: 0 })
    },
    DISPLAY () {
      return DISPLAY
    },
    folderIdFromRoute () {
      return this.$route.params.childId || this.$route.params.folderId || null
    },
    isRecentRoomsRoute() {
      return this.$route.params.childId === 'recent' || this.$route.params.folderId === 'recent'
    },
    hasActiveFilters() {
      return Object.values(this.filtersPane.filters).some(value => {
        if (Array.isArray(value)) {
          return value.length > 0
        }
        return !!value
      })
    },
    isSearchModeOrFiltersEnabled() {
      return this.searchModeEnabled || this.hasActiveFilters
    },
    roomsByRole () {
      return {
        asPM: this.pmRooms,
        asGuest: this.guestRooms,
      }
    },
  },
  watch: {
    $route: {
      handler: async function (to) {
        if (this.isFullAccessLogin) {
          this.initPaginationFromUserSettings()
          if (to.params.childId) {
            this.initFolder(to.params.childId)
          } else if (to.params.folderId) {
            if (to.params.folderId === 'recent') {
              this.killFolder()
              this.initRecentProjects()
            } else {
              this.initFolder(to.params.folderId)
            }
          } else {
            this.killFolder()
            this.fetchRooms()
          }
        }
        this.resetSearch()
      },
      immediate: true,
    },
    searchQuery () {
      this.resetPaginationPages()
      if (this.searchQuery) {
        this.SET_SEARCH_MODE_ENABLED(true)
        this.debouncedSearch(this.searchQuery)
      } else if (this.isRecentRoomsRoute) {
        this.SET_SEARCH_MODE_ENABLED(false)
        this.initRecentProjects()
      } else {
        this.SET_SEARCH_MODE_ENABLED(false)
        this.fetchRooms()
      }
    },
    'filtersPane.filters': {
      handler() {
        this.resetPaginationPages()
        if (this.searchModeEnabled) {
          this.fetchSearchPage()
        } else if (this.isRecentRoomsRoute) {
          this.hasActiveFilters ? this.fetchRooms() : this.initRecentProjects()
        } else {
          this.fetchRooms()
        }
      },
      deep: true,
    },
    display(newDisplay, oldDisplay) {
      if (newDisplay !== oldDisplay) {
        this.initPaginationFromUserSettings()
        this.sorting.pm = {}
        this.sorting.guest = {}

        if (this.searchModeEnabled) {
          this.fetchSearchPage()
        } else if (this.isRecentRoomsRoute) {
          this.initRecentProjects()
        } else {
          this.fetchRooms()
        }
      }
    },
  },
  created () {
    this.debouncedSearch = debounce(this.fetchSearchPage, SEARCH_DEBOUNCE_RATE)
    const receivedMnemoUnderNda = this.$route?.query?.mnemoUnderNda
    if (receivedMnemoUnderNda) {
      this.SET_ROOM_UNDER_NDA_DIALOG({ mnemo: receivedMnemoUnderNda, isOpen: true })
      // Remove the query param.
      this.$router.replace({ ...this.$route, query: { ...this.$route.query, mnemoUnderNda: undefined } })
    }
  },
  mounted () {
    this.SET_HOME_LAYOUT(true)
    this.changeDisplay(this.displayType)
  },
  beforeUnmount () {
    this.SET_HOME_LAYOUT(false)
    this.killFolder()
  },
  methods: {
    ...mapMutations('projectsFolders', [SET_CURRENT_PROJECTS_FOLDER]),
    ...mapMutations('rooms', [SET_SEARCH_MODE_ENABLED]),
    ...mapMutations('global', [GlobalMutationTypes.SET_HOME_LAYOUT]),
    ...mapActions('rooms', [GET_RECENT_ROOMS]),
    ...mapActions('projectsFolders', [GET_PROJECTS_FOLDER_BY_ID]),
    ...mapMutations('room', [SET_ROOM_UNDER_NDA_DIALOG]),
    changeDisplay (display) {
      this.display = display
    },
    async initFolder (folderId) {
      try {
        await this.GET_PROJECTS_FOLDER_BY_ID({
          folderId,
        })

        this.resetPaginationPages()
        await this.fetchRooms()
      } catch (error) {
        // Return to previous URL
        this.$router.go(-1)
        toast.error(this.$t('project.documents.dialogs.DocumentsNewFolderDialog.getFolderError', { name: '' }))
      }
    },
    async initRecentProjects () {
      try {
        await this.GET_RECENT_ROOMS({
          limit: RECENT_ROOMS_LIMIT,
        })
      } catch (error) {
        // Return to previous URL
        this.$router.go(-1)
        toast.error(this.$t('project.documents.dialogs.DocumentsNewFolderDialog.getFolderError', { name: '' }))
      }
    },
    killFolder () {
      this.SET_CURRENT_PROJECTS_FOLDER({})
    },
    async fetchSearchPage() {
      await Promise.all([
        this.fetchRoomsByRole('pm', { search: this.searchQuery }),
        this.fetchRoomsByRole('guest', { search: this.searchQuery }),
      ])
    },
    async fetchRooms() {
      await Promise.all([
        this.fetchRoomsByRole('pm'),
        this.fetchRoomsByRole('guest'),
      ])
    },
    fetchRoomsByRole(role, { search = '' } = {}) {
      const isPM = role === 'pm'
      const action = isPM ? 'GET_PM_ROOMS_PROGRESSIVELY' : 'GET_GUEST_ROOMS_PROGRESSIVELY'
      const pagination = isPM ? this.pmPagination : this.guestPagination

      // If the role filter is exclusive to the other role, skip completely
      const roleFilter = this.filtersPane.filters.role
      if (
        roleFilter?.length === 1 &&
        ((isPM && roleFilter[0] === 'guest') || (!isPM && roleFilter[0] === 'projectManager'))
      ) {
        this.$store.commit(`rooms/SET_${role.toUpperCase()}_ROOMS`, [])
        return
      }

      const baseQuery = {
        ...this.getFiltersParams(this.filtersPane.filters),
        ...this.getSortingParams(role),
        page: pagination.page,
        pageSize: pagination.pageSize,
        mini: true,
        roomCounters: false,
      }

      if (typeof search === 'string' && search.trim() !== '') {
        baseQuery.search = search
      }

      if (this.folderIdFromRoute && !this.isRecentRoomsRoute) {
        baseQuery.folder = this.folderIdFromRoute
      } else {
        baseQuery.excludeRoomsInFolders = true
      }
      this.$store.commit(`rooms/SET_BASE_QUERY_PARAMS_${role.toUpperCase()}`, baseQuery)

      return this.$store.dispatch(`rooms/${action}`, baseQuery)
    },
    onPageChange({ role, page }) {
      this.handlePaginationUpdate({ role, changes: { page } })
    },
    onPageSizeChange({ role, pageSize }) {
      setUserSetting(
        this.currentUserId,
        `projets-${this.display === DISPLAY.grid ? 'grid' : 'table'}-items-per-page-${role}`,
        pageSize,
      )

      this.handlePaginationUpdate({ role, changes: { page: 1, pageSize } })
    },
    onSortChange({ role, sorting }) {
      this.handlePaginationUpdate({ role, changes: { page: 1 }, sorting })
    },
    handlePaginationUpdate({ role, changes, sorting = null }) {
      this.updatePaginationForRole(role, changes)

      if (sorting) {
        this.sorting[role] = sorting
      }

      if (this.searchModeEnabled) {
        this.fetchSearchPage()
      } else {
        this.fetchRoomsByRole(role)
      }
    },
    updatePaginationForRole(role, changes) {
      const isPM = role === 'pm'
      const pagination = isPM ? this.pmPagination : this.guestPagination
      const mutation = isPM ? 'SET_PM_ROOMS_PAGINATION' : 'SET_GUEST_ROOMS_PAGINATION'

      this.$store.commit(`rooms/${mutation}`, {
        ...pagination,
        ...changes,
      })
    },
    initPaginationFromUserSettings() {
      ['pm', 'guest'].forEach(role => {
        const pageSize = getUserSetting(
          this.currentUserId,
          `projets-${this.display === DISPLAY.grid ? 'grid' : 'table'}-items-per-page-${role}`,
        )

        this.updatePaginationForRole(role, {
          pageSize: pageSize || DEFAULT_PAGE_SIZE,
          page: 1,
        })
      })
    },
    resetPaginationPages() {
      ['pm', 'guest'].forEach(role => {
        this.updatePaginationForRole(role, { page: 1 })
      })
    },
    getSortingParams(role) {
      const sorting = this.sorting?.[role] || {}
      if (sorting.name && sorting.order) {
        return {
          sortBy: sorting.name,
          sortDir: sorting.order,
        }
      }
      return {}
    },
    getFiltersParams(filters) {
      const typeMap = {
        project: "lp",
        dataroom: "dr",
      }
      const roleMap = {
        projectManager: true,
        guest: false,
      }
      const types = filters.projectType?.map(p => typeMap[p]).filter(Boolean) || []
      const roles = filters.role?.map(r => roleMap[r]).filter(r => r !== undefined) || []

      const params = {
        type: types.length ? types.join(',') : undefined,
        isPM: roles.length ? roles.join(',') : undefined,
        creationDateFrom: filters.creationDate?.[0],
        creationDateTo: filters.creationDate?.[1],
        expectedClosingDateFrom: filters.dueDate?.[0],
        expectedClosingDateTo: filters.dueDate?.[1],
        labelIds: filters.selectedLabels?.length
          ? filters.selectedLabels.map(l => l.id).join(',')
          : undefined,
        // Remove metadata keys with empty values
        metadata: filters.metadata
          ? Object.fromEntries(
              Object.entries(filters.metadata).filter(
                ([, v]) => Array.isArray(v) && v.length > 0,
              ),
            )
          : undefined,
      }
      // Remove keys with null, undefined, or empty values
      return Object.fromEntries(
        Object.entries(params).filter(([, value]) =>
          value !== undefined && value !== null && value !== '',
        ),
      )
    },
    resetSearch () {
      // Even if it's done in the action itself since we et the searchQuery to an empty string
      // We empty the state right away to be more responsive in that instance
      this.SET_SEARCH_MODE_ENABLED(false)
      this.searchQuery = ''
    },
  },
}
</script>

<style scoped>
/* transition list */
.list-complete-item {
  transition: all 0.5s;
}

.list-complete-enter,
.list-complete-leave-to {
  opacity: 0;
}

.list-complete-leave-active {
  position: absolute;
  z-index: -1;
}
</style>
